(() => {
  const initFaq = (root) => {
    if (!root || root.dataset.lbFaqReady === 'true') return;
    root.dataset.lbFaqReady = 'true';

    const normalize = (value) => (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ñ/g, 'n')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const tokenise = (value) => normalize(value).split(' ').filter(Boolean);

    const aliasMap = {
      envio: ['envio', 'envios', 'enviar', 'enviado', 'entrega', 'entregar', 'seur', 'plazo', 'plazos', 'mensajeria'],
      envios: ['envio', 'envios', 'enviar', 'enviado', 'entrega', 'entregar', 'seur', 'plazo', 'plazos', 'mensajeria'],
      entrega: ['entrega', 'entregar', 'envio', 'envios', 'seur', 'plazo', 'plazos', 'mensajeria'],
      garantia: ['garantia', 'garantias', 'cobertura', 'incidencia', 'incidencias', 'averia', 'averias'],
      garantias: ['garantia', 'garantias', 'cobertura', 'incidencia', 'incidencias', 'averia', 'averias'],
      devolucion: ['devolucion', 'devoluciones', 'cambio', 'cambios', 'retorno', 'reembolso'],
      devoluciones: ['devolucion', 'devoluciones', 'cambio', 'cambios', 'retorno', 'reembolso'],
      financiacion: ['financiacion', 'financiar', 'pago', 'pagos', 'cuotas', 'aplazado', 'klarna'],
      financiar: ['financiacion', 'financiar', 'pago', 'pagos', 'cuotas', 'aplazado', 'klarna'],
      pago: ['pago', 'pagos', 'financiacion', 'financiar', 'cuotas', 'tarjeta'],
      pagos: ['pago', 'pagos', 'financiacion', 'financiar', 'cuotas', 'tarjeta'],
      talla: ['talla', 'tallas', 'altura', 'medida', 'medidas', 'postura', 'comodidad', 'cuadro'],
      tallas: ['talla', 'tallas', 'altura', 'medida', 'medidas', 'postura', 'comodidad', 'cuadro'],
      autonomia: ['autonomia', 'bateria', 'baterias', 'km', 'kilometros', 'rango', 'distancia', 'wh'],
      bateria: ['bateria', 'baterias', 'autonomia', 'carga', 'cargar', 'wh', 'kilometros', 'km'],
      baterias: ['bateria', 'baterias', 'autonomia', 'carga', 'cargar', 'wh', 'kilometros', 'km'],
      motor: ['motor', 'motores', 'asistencia', 'velocidad', 'par', 'nm', 'bosch', 'yamaha', 'shimano', 'fazua'],
      velocidad: ['velocidad', 'limite', 'limitacion', 'asistencia', 'motor', '25', 'kmh'],
      asistencia: ['asistencia', 'motor', 'ayuda', 'modo', 'modos', 'velocidad'],
      montaje: ['montaje', 'montada', 'montado', 'preparacion', 'ajuste', 'revision'],
      stock: ['stock', 'disponibilidad', 'disponible', 'agotado', 'unidades'],
      disponible: ['disponible', 'disponibilidad', 'stock', 'unidades'],
      tienda: ['tienda', 'tiendas', 'labosch', 'asesoria', 'asesoramiento', 'probar'],
      tiendas: ['tienda', 'tiendas', 'labosch', 'asesoria', 'asesoramiento', 'probar'],
      mantenimiento: ['mantenimiento', 'revision', 'revisiones', 'averia', 'averias', 'taller', 'cadena', 'frenos'],
      averia: ['averia', 'averias', 'mantenimiento', 'taller', 'garantia', 'incidencia'],
      averias: ['averia', 'averias', 'mantenimiento', 'taller', 'garantia', 'incidencia'],
      seguro: ['seguro', 'garantia', 'cobertura', 'robo'],
      ebike: ['ebike', 'e bike', 'bicicleta electrica', 'bicicletas electricas'],
      bicicleta: ['bicicleta', 'bicicletas', 'ebike', 'e bike', 'modelo', 'modelos'],
      gravel: ['gravel'],
      mtb: ['mtb', 'montana', 'mountain'],
      montana: ['montana', 'mtb', 'mountain'],
      urbana: ['urbana', 'ciudad', 'city'],
      trekking: ['trekking', 'hibrida', 'hibridas'],
      hibrida: ['hibrida', 'hibridas', 'trekking'],
      hibridas: ['hibrida', 'hibridas', 'trekking'],
      carga: ['carga', 'cargo', 'familia', 'transporte']
    };

    const significantTokens = (value) => tokenise(value).filter((token) => {
      if (['km', 'wh', 'nm', '25'].includes(token)) return true;
      return token.length >= 3;
    });

    const expandToken = (token) => {
      const cleanToken = normalize(token);
      const aliases = new Set([cleanToken]);
      if (!cleanToken || cleanToken.length < 3) return Array.from(aliases).filter(Boolean);

      Object.keys(aliasMap).forEach((key) => {
        const normalizedKey = normalize(key);
        const group = aliasMap[key].map(normalize).filter(Boolean);
        const keyMatches = normalizedKey.startsWith(cleanToken) || cleanToken.startsWith(normalizedKey);
        const groupMatches = group.some((term) => term.length >= 3 && (term.startsWith(cleanToken) || cleanToken.startsWith(term)));

        if (keyMatches || groupMatches) {
          group.forEach((term) => aliases.add(term));
        }
      });

      return Array.from(aliases).filter(Boolean);
    };

    const levenshteinDistance = (a, b) => {
      if (a === b) return 0;
      if (!a || !b) return Math.max(a.length, b.length);
      const matrix = Array.from({ length: a.length + 1 }, (_, i) => [i]);
      for (let j = 1; j <= b.length; j += 1) matrix[0][j] = j;
      for (let i = 1; i <= a.length; i += 1) {
        for (let j = 1; j <= b.length; j += 1) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j - 1] + cost
          );
        }
      }
      return matrix[a.length][b.length];
    };

    const exactOrPrefixTokenMatch = (hayTokens, candidate) => hayTokens.some((hayToken) => {
      if (!hayToken || !candidate) return false;
      if (hayToken === candidate) return true;
      if (candidate.length >= 4 && hayToken.startsWith(candidate)) return true;
      if (hayToken.length >= 5 && candidate.length >= 5) {
        const maxDistance = Math.min(candidate.length, hayToken.length) >= 8 ? 2 : 1;
        return levenshteinDistance(candidate, hayToken) <= maxDistance;
      }
      return false;
    });

    const tokenMatchesHaystack = (hay, hayTokens, token) => {
      const cleanToken = normalize(token);
      const candidates = expandToken(cleanToken).filter((candidate) => candidate.length >= 3 || ['km', 'wh', 'nm', '25'].includes(candidate));

      return candidates.some((candidate) => {
        if (!candidate) return false;

        // Coincidencia literal/prefijo de la palabra buscada. Ej.: envi -> envio/envios.
        if (exactOrPrefixTokenMatch(hayTokens, cleanToken)) return true;

        // Alias controlados. Evita términos demasiado genéricos para que "envio" no devuelva
        // preguntas de compra solo porque aparece "transporte" o una palabra tangencial.
        return exactOrPrefixTokenMatch(hayTokens, candidate);
      });
    };

    const smartMatches = (haystack, query) => {
      const q = normalize(query);
      if (!q) return true;

      const queryTokens = significantTokens(q);
      if (!queryTokens.length) return true;

      const hay = normalize(haystack);
      const hayTokens = tokenise(hay).filter((token) => token.length >= 2);

      return queryTokens.every((token) => tokenMatchesHaystack(hay, hayTokens, token));
    };

    const items = Array.from(root.querySelectorAll('[data-lb-faq-item]'));
    const categories = Array.from(root.querySelectorAll('[data-lb-faq-category]'));
    const search = root.querySelector('[data-lb-faq-search]');
    const clear = root.querySelector('[data-lb-faq-clear]');
    const filters = Array.from(root.querySelectorAll('[data-lb-faq-filter]'));
    const count = root.querySelector('[data-lb-faq-result-count]');
    const empty = root.querySelector('[data-lb-faq-empty]');
    let activeFilter = 'all';

    items.forEach((item, index) => {
      item.style.setProperty('--lb-stagger', String(index % 7));
      const button = item.querySelector('.lb-faq-question');
      if (!button) return;
      const answer = item.querySelector('.lb-faq-answer');
      if (answer) {
        if (!button.id && answer.id) button.id = `${answer.id}-trigger`;
        if (button.id) answer.setAttribute('aria-labelledby', button.id);
        answer.setAttribute('aria-hidden', 'true');
        answer.inert = true;
      }
      button.setAttribute('aria-expanded', 'false');
      item.classList.remove('is-open');
      item.dataset.lbFaqSearchIndex = [
        item.dataset.search || '',
        item.textContent || '',
        item.dataset.category || ''
      ].join(' ');
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        const nextExpanded = !expanded;
        button.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
        item.classList.toggle('is-open', nextExpanded);
        if (answer) {
          answer.setAttribute('aria-hidden', nextExpanded ? 'false' : 'true');
          answer.inert = !nextExpanded;
        }
      });
    });

    const update = () => {
      const q = search?.value || '';
      const hasQuery = normalize(q).length > 0;
      let visibleItems = 0;

      categories.forEach((category) => {
        const categoryKey = category.dataset.category;
        let categoryVisible = 0;
        category.querySelectorAll('[data-lb-faq-item]').forEach((item) => {
          const matchesFilter = hasQuery ? true : (activeFilter === 'all' || item.dataset.category === activeFilter);
          const matchesSearch = !hasQuery || smartMatches(item.dataset.lbFaqSearchIndex || item.dataset.search || item.textContent || '', q);
          const isVisible = matchesFilter && matchesSearch;
          item.hidden = !isVisible;
          item.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
          item.classList.toggle('lb-faq-is-hidden', !isVisible);
          item.style.display = isVisible ? '' : 'none';

          if (!isVisible) {
            const button = item.querySelector('.lb-faq-question');
            const answer = item.querySelector('.lb-faq-answer');
            if (button) button.setAttribute('aria-expanded', 'false');
            if (answer) {
              answer.setAttribute('aria-hidden', 'true');
              answer.inert = true;
            }
            item.classList.remove('is-open');
          }

          if (isVisible) {
            categoryVisible += 1;
            visibleItems += 1;
          }
        });

        const showCategory = categoryVisible > 0 && (hasQuery || activeFilter === 'all' || categoryKey === activeFilter);
        category.hidden = !showCategory;
        category.setAttribute('aria-hidden', showCategory ? 'false' : 'true');
        category.classList.toggle('lb-faq-is-hidden', !showCategory);
        category.style.display = showCategory ? '' : 'none';
      });

      root.classList.toggle('lb-faq-is-searching', hasQuery);
      root.classList.toggle('lb-faq-has-no-results', visibleItems === 0);
      if (count) count.textContent = String(visibleItems);
      if (empty) {
        empty.hidden = visibleItems > 0;
        empty.style.display = visibleItems > 0 ? 'none' : '';
      }
    };

    filters.forEach((button) => {
      button.classList.toggle('is-active', (button.dataset.lbFaqFilter || 'all') === activeFilter);
      button.addEventListener('click', () => {
        activeFilter = button.dataset.lbFaqFilter || 'all';
        filters.forEach((candidate) => candidate.classList.toggle('is-active', candidate === button));
        update();
        const firstVisible = categories.find((category) => !category.hidden);
        if (firstVisible) {
          const y = firstVisible.getBoundingClientRect().top + window.scrollY - 170;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        }
      });
    });

    if (search) {
      search.addEventListener('input', update);
      search.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          search.value = '';
          update();
        }
      });
    }

    if (clear) {
      clear.addEventListener('click', () => {
        if (search) search.value = '';
        update();
        search?.focus();
      });
    }

    const supportTriggers = Array.from(root.querySelectorAll('[data-lb-faq-open-support]'));
    supportTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const headerRoot = document.querySelector('[data-labosch-simple-header]');
        if (!headerRoot) return;

        const kind = trigger.getAttribute('data-lb-support-kind') || 'commercial';
        const title = trigger.getAttribute('data-lb-support-title') || 'Atención comercial';
        const email = trigger.getAttribute('data-lb-support-email') || 'web@labosch.es';
        const subject = trigger.getAttribute('data-lb-support-subject') || 'Consulta FAQ LaBosch';

        event.preventDefault();
        event.stopPropagation();

        const headerTrigger = headerRoot.querySelector(`[data-lb-support-open][data-lb-support-kind="${kind}"]`);
        if (headerTrigger) {
          headerTrigger.click();
          return;
        }

        const proxy = document.createElement('button');
        proxy.type = 'button';
        proxy.hidden = true;
        proxy.setAttribute('data-lb-support-open', '');
        proxy.setAttribute('data-lb-support-kind', kind);
        proxy.setAttribute('data-lb-support-title', title);
        proxy.setAttribute('data-lb-support-email', email);
        proxy.setAttribute('data-lb-support-subject', subject);
        headerRoot.appendChild(proxy);
        proxy.click();
        window.setTimeout(() => proxy.remove(), 0);
      });
    });

    const tilt = root.querySelector('[data-lb-faq-tilt]');
    if (tilt && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      let raf = 0;
      tilt.addEventListener('pointermove', (event) => {
        window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(() => {
          const rect = tilt.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          tilt.style.setProperty('--faq-ry', `${x * 9}deg`);
          tilt.style.setProperty('--faq-rx', `${y * -7}deg`);
        });
      });
      tilt.addEventListener('pointerleave', () => {
        tilt.style.setProperty('--faq-ry', '0deg');
        tilt.style.setProperty('--faq-rx', '0deg');
      });
    }

    const revealTargets = Array.from(root.querySelectorAll('[data-lb-reveal]'));
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      revealTargets.forEach((target) => observer.observe(target));
    } else {
      revealTargets.forEach((target) => target.classList.add('is-visible'));
    }

    update();
  };

  const initAll = () => document.querySelectorAll('[data-lb-faq]').forEach(initFaq);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
